/**
 * Pricing Plan Helper Functions
 * These utilities help with detecting changes and retirement conditions for pricing plans
 */
import { PLAN_TYPE, RETIREMENT_TYPE } from "../../Utils/constants";

/**
 * Check if a tier needs to be retired based on changes to its configuration
 * Retirement is triggered when:
 * - Base price changes
 * - Container is added or removed
 * - Quota or overage fee changes
 * - Endpoint or channel is REMOVED from a container
 * Retirement is NOT triggered when:
 * - New endpoint/channel is added to a container
 * @param {Object} currentTier - The current tier configuration
 * @param {Object} originalTier - The original tier configuration from the server
 * @returns {boolean} - True if the tier should be retired
 */
export const checkTierRetirement = (currentTier, originalTier) => {
  if (!originalTier || !currentTier) {
    return false;
  }

  // Check if base price changed
  const currentBasePrice = parseFloat(currentTier.baseValue) || 0;
  const originalBasePrice = parseFloat(originalTier.basePrice) || 0;
  if (currentBasePrice !== originalBasePrice) {
    return true;
  }

  const originalContainers = originalTier.containers || [];
  const currentContainers = currentTier.containers || [];

  // Build a Map for O(1) lookup of current containers by ID
  const currentContainerMap = new Map();
  for (const c of currentContainers) {
    const id = c.id || c.containerId;
    if (id) {
      currentContainerMap.set(id, c);
    }
  }

  // Build a Map for O(1) lookup of original containers by ID
  const originalContainerMap = new Map();
  for (const c of originalContainers) {
    const id = c.id || c.containerId;
    if (id) {
      originalContainerMap.set(id, c);
    }
  }

  // Check if any original containers were removed
  for (const orig of originalContainers) {
    const origId = orig.id || orig.containerId;
    if (!currentContainerMap.has(origId)) {
      return true;
    }
  }

  // Check if any current containers were added or existing containers have changes
  for (const curr of currentContainers) {
    const currId = curr.id || curr.containerId;
    if (!currId || !originalContainerMap.has(currId)) {
      return true;
    }

      const orig = originalContainerMap.get(currId);
      if (orig) {
        // Check quota changes
        const currQuota = parseFloat(curr.quotaLimit ?? curr.monthlyQuota ?? curr.quota) || 0;
        const origQuota = parseFloat(orig.quotaLimit ?? orig.monthlyQuota ?? orig.quota) || 0;
        if (currQuota !== origQuota) {
          return true;
        }

        // Check overage changes
        const currOverage = parseFloat(curr.overages ?? curr.overageFee ?? curr.overage) || 0;
        const origOverage = parseFloat(orig.overages ?? orig.overageFee ?? orig.overage) || 0;
        if (currOverage !== origOverage) {
          return true;
        }

        // Check endpoint changes - only trigger retirement if endpoint was REMOVED
        // Use Set for O(1) lookup instead of Array.includes()
        const currEndpointSet = new Set((curr.endpoints || []).map(e => e.id || e.endpointId || e));
        const origEndpoints = (orig.endpoints || []).map(e => e.id || e.endpointId || e);

        for (const e of origEndpoints) {
          if (!currEndpointSet.has(e)) {
            return true;
          }
        }

        // Check channel changes - only trigger retirement if channel was REMOVED
        // Use Set for O(1) lookup instead of Array.includes()
        const currChannelSet = new Set((curr.channels || []).map(c => c.id || c.channelId || c));
        const origChannels = (orig.channels || []).map(c => c.id || c.channelId || c);

        for (const c of origChannels) {
          if (!currChannelSet.has(c)) {
            return true;
        }
      }
    }
  }

  return false;
};

/**
 * Compute the install fee state from plan details
 * @param {Object} planDetails - The plan details object from the server
 * @returns {Object} - Object with isEnabled and amount properties
 */
export const computeInstallFeeState = (planDetails) => {
  const hasInstallFee = planDetails?.installFee != null && planDetails?.installFee !== "";
  const hasInstallFeeCatalog = planDetails?.installFeeCatalog != null && planDetails?.installFeeCatalog !== "";
  const isEnabled = hasInstallFee && hasInstallFeeCatalog;
  const amount = isEnabled ? (planDetails.installFee != null ? planDetails.installFee : "") : "";
  
  return { isEnabled, amount };
};

/**
 * Check if the install fee has changed
 * @param {boolean} currentEnabled - Current install fee enabled state
 * @param {string|number} currentAmount - Current install fee amount
 * @param {boolean} originalEnabled - Original install fee enabled state
 * @param {string|number} originalAmount - Original install fee amount
 * @returns {boolean} - True if the install fee has changed
 */
export const hasInstallFeeChanged = (currentEnabled, currentAmount, originalEnabled, originalAmount) => {
  if (currentEnabled !== originalEnabled) {
    return true;
  }
  if (currentEnabled && currentAmount !== String(originalAmount)) {
    return true;
  }
  return false;
};

/**
 * Helper function to transform container format from Redux to API
 */
export const transformContainerForAPI = (container) => {
  const apiContainer = {
    name: container.name,
    description: container.description,
    monthlyQuota: parseFloat(container.quotaLimit ?? container.monthlyQuota ?? container.quota) || 0,
    overageFee: parseFloat(container.overages ?? container.overageFee ?? container.overage) || 0,
    isFree: false,
    endpoints: (container.endpoints || []).map(ep => ep.id || ep.endpointId || ep),
    channels: (container.channels || []).map(ch => ch.id || ch.channelId || ch),
  };

  if (container.id || container.containerId) {
    apiContainer.containerId = container.id || container.containerId;
  }

  return apiContainer;
};

/**
 * Transform tier format from Redux to API
 * Includes priceId for existing tiers (used when updating a plan)
 * @param {Object} tier - The tier object from Redux state
 * @param {string} defaultTitle - Default title if tier.title is missing
 * @returns {Object} - Tier object formatted for API
 */
export const transformTierForAPI = (tier, defaultTitle = "Tier") => {
  const apiTier = {
    isActive: !!tier.isActive,
    title: tier.title || defaultTitle,
    basePrice: parseFloat(tier.baseValue) || 0,
    description: tier.description || "",
    containers: (tier.containers || []).map(transformContainerForAPI),
  };

  // Include priceId only for existing tiers (when updating an existing plan)
  // New tiers won't have a priceId
  if (tier.priceId) {
    apiTier.priceId = tier.priceId;
  }

  return apiTier;
};

/**
 * Determine the retirement type based on plan changes
 */
export const determineRetirementType = ({
  planDetails,
  isInstallFeeEnabled,
  installFee,
  planType,
  config,
  monthCost,
  originalPrice,
  planVisibility,
  PLAN_VISIBILITY,
  tiers
}) => {
  let isWholePlanRetiring = false;
  let isTierRetiring = false;

  // Check if install fee has changed
  const originalInstallFeeState = computeInstallFeeState(planDetails);
  const currentInstallFeeAmount = isInstallFeeEnabled ? String(installFee ?? "").replace(/,/g, "") : "";
  
  if (hasInstallFeeChanged(isInstallFeeEnabled, currentInstallFeeAmount, originalInstallFeeState.isEnabled, originalInstallFeeState.amount)) {
    isWholePlanRetiring = true;
  }

  // Check if price has changed for subscription plans
  if (planType?.value === PLAN_TYPE.SUBSCRIPTION || !config.isAdvancedPricePlanEnabled) {
    const currentPrice = String(monthCost ?? "").replace(/,/g, "");
    const priceChanged = currentPrice !== originalPrice;
    if (priceChanged) {
      isWholePlanRetiring = true;
    }
  } else if (planType?.value === PLAN_TYPE.ADVANCED) {
    // Check if any tiers have changed for advanced plans
    const origTiers = planDetails?.tiers || {};

    // Check retirement for each tier based on visibility
    if (planVisibility === PLAN_VISIBILITY.PUBLIC) {
      if (checkTierRetirement(tiers.basic, origTiers.basic)) {
        isTierRetiring = true;
      }
      if (checkTierRetirement(tiers.plus, origTiers.plus)) {
        isTierRetiring = true;
      }
      if (checkTierRetirement(tiers.pro, origTiers.pro)) {
        isTierRetiring = true;
      }
    } else {
      if (checkTierRetirement(tiers.custom, origTiers.custom)) {
        isTierRetiring = true;
      }
    }
  }

  if (isWholePlanRetiring) return RETIREMENT_TYPE.WHOLE;
  if (isTierRetiring) return RETIREMENT_TYPE.TIER;
  return RETIREMENT_TYPE.NONE;
};
